import { apiConfig, authenticationConfig, cacheConfig } from "app-config";
import AuthService from "services/authService";
import type User from "models/User";
import networkService from "./networkService";
import Errors from "constants/Errors";

/**
 * Génère les headers pour la requête http vers l'API graph
 * @returns
 */
async function createHeaders(): Promise<HeadersInit> {
  const accessToken = await AuthService.getAccessToken(apiConfig.azureAd.scopes);
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    ConsistencyLevel: "eventual",
  };
}

const cacheName = cacheConfig.agents.name;

/**
 * Récupère la totalité des agents
 * @param selectFilter les informations supplémentaires précises que
 * l'on souhaite récupérer sur les agents
 * @returns
 */
async function getAllAgents(selectFilter: Array<string>): Promise<Array<User> | undefined> {
  const options = {
    method: "GET",
    headers: await createHeaders(),
  };

  let url = `${authenticationConfig.graph.graphEndpoint}/users?$top=999`;
  let filters = ["surname", "givenName", "displayName", "id", "jobTitle"];
  if (selectFilter.length > 0) {
    filters = filters.concat(selectFilter);
  }
  url += "&$select=" + filters.toString();
  url += "&$filter=employeeId ne null&$count=true";

  try {
    const response = await networkService.fetchWithCacheFallback(url, options, cacheName);
    return networkService.getJsonValue<Array<User>>(response);
  } catch (error: unknown) {
    console.error(error);
  }
}

/**
 * Récupère le manager d'un agent
 * @param id l'id de l'agent
 * @returns
 */
async function getManagerByAgent(id: string): Promise<User | undefined> {
  // Sur un malentendu, l'id peut être null ou vide, il n'est pas utile de faire un appel à l'API
  if (id == null || id.trim() === "") {
    return undefined;
  }

  const url = `${authenticationConfig.graph.graphEndpoint}/users/${id}/manager`;
  const options = {
    method: "GET",
    headers: await createHeaders(),
  };

  try {
    const response = await networkService.fetchWithCacheFallback(url, options, cacheName);
    return (await response?.json()) as User;
  } catch (error) {
    console.error(error);
  }
}

/**
 * Récupère les subordonnés d'un manager
 * @returns Un tableau des ids des subordonnés ou void
 */
async function getDirectReports(): Promise<Array<string>> {
  const url = `${authenticationConfig.graph.graphEndpoint}/me/directReports?$select=employeeId`;
  const options = {
    method: "GET",
    headers: await createHeaders(),
  };

  try {
    const response = await networkService.fetchWithCacheFallback(url, options, cacheName);
    const data = await networkService.getJsonValue<Array<User>>(response);
    return data.length > 0 ? data.map((o: User) => o?.id) : [];
  } catch (error: unknown) {
    console.error("Error getting Direct Reports", error);
    return [];
  }
}

/**
 * Récupère les informations de base de l'utilisateur
 * @returns l'utilisateur
 */
async function getUserInfo(): Promise<User> {
  const userInfos = ["displayName", "employeeId", "mail", "id", "jobTitle"];
  const url = `${authenticationConfig.graph.graphEndpoint}/me?$select=${userInfos.join(",")}`;
  const options = {
    method: "GET",
    headers: await createHeaders(),
  };

  try {
    const response = await networkService.fetchWithCacheFallback(url, options, cacheName);
    return (await response.json()) as User;
  } catch (error: unknown) {
    console.error(error);
    throw new Error(Errors.SERVER_ERROR, { cause: error });
  }
}

/**
 * Récupère l'image de profil de l'utilisateur
 * @returns string contenant l'image ou undefined
 */
async function getUserPhoto(): Promise<string | undefined> {
  const url = `${authenticationConfig.graph.graphEndpoint}/me/photos/48x48/$value`;
  const options = {
    method: "GET",
    headers: await createHeaders(),
  };

  try {
    const response = await fetch(url, options);
    const blob = await response.blob();
    const base64data = await readFileAsDataURL(blob);
    if (base64data != null) {
      if (typeof base64data === "string") {
        return base64data;
      } else {
        return new TextDecoder().decode(base64data);
      }
    }
  } catch (error) {
    console.error(error);
    return undefined;
  }
}

async function readFileAsDataURL(file: Blob): Promise<string | ArrayBuffer | null> {
  // eslint-disable-next-line promise/avoid-new
  const resultBase64 = await new Promise<string | ArrayBuffer | null>((resolve) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.readAsDataURL(file);
  });

  return resultBase64;
}

const graphService = {
  getAllAgents,
  getUserInfo,
  getUserPhoto,
  getManagerByAgent,
  getDirectReports,
};

export default graphService;
